/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { createOperationCall, createOperationCallWithJobSwaggers, OperationHandler } from 'src/core/services/api/createOperationCall';
import { ResourceListResults } from 'src/core/types/ResourceListTypes';
import { httpClient } from 'src/core/utils/api/httpClient';
import { luisRequestOptionsBuilder } from 'src/core/utils/api/luisRequestOptionsBuilder';
import { urlBuilder } from 'src/core/utils/api/urlBuilder';
import { createService } from 'src/core/utils/createService';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { ModelGuidanceReportSwagger } from 'src/customText/core/services/modelGuidance/customTextModelGuidanceSwaggers';
import { CustomTextModelEvaluationSwagger } from 'src/customText/core/services/trainedModels/customTextModelEvaluationSwagger';
import { CustomTextModelVerificationSwagger } from 'src/customText/core/services/trainedModels/customTextModelVerificationSwagger';
import { LoadSnapshotJobStatusSwagger } from 'src/modules/modelDetails/types/loadSnapshotJobStatusSwagger';
import { EvaluationOnDemandJobInputSwagger, EvaluationOnDemandJobSwagger } from 'src/modules/modelEvaluation/modelEvaluationSwaggers';
import { TrainedModelSwagger } from 'src/modules/modelTraining/types/trainingSwaggers';

export const getCustomTextTrainedModelService = createService(({ context, getEnvironment }) => {
    const { apiUrl, isProduction, webApiUrl } = getEnvironment({ apiName: 'customText' });
    const { serviceName } = getPortalFromUrl();
    const apiVersion =
        serviceName === 'summarization'
            ? LuisConstants.Summarization.AbstractiveAuthoringVersion
            : LuisConstants.CustomText.AuthoringApiVersion;

    const apiOptions = luisRequestOptionsBuilder()
        .useDefaultOptions(context.serviceKey, isProduction)
        .build();

    const getUrlBuilder = (projectName: string, trainedModelName?: string) =>
        urlBuilder()
            .baseUrl(apiUrl)
            .project(projectName)
            .model(trainedModelName)
            .queryParam('api-version', apiVersion);

    const getModelGuidanceUrl = (projectName: string, trainedModelName: string) =>
        urlBuilder()
            .baseUrl(webApiUrl)
            .resource('internal')
            .project(projectName)
            .model(trainedModelName)
            .resource('modelguidance')
            .queryParam('api-version', LuisConstants.CustomText.InternalApiVersion)
            .build();

    const getLoadSnapshotUrl = (projectName: string, trainedModelName: string) =>
        urlBuilder()
            .baseUrl(apiUrl)
            .project(projectName)
            .model(trainedModelName)
            .resource(':load-snapshot')
            .queryParam('api-version', apiVersion)
            .build();

    const getRefreshEvaluationUrl = (projectName: string, trainedModelName: string) =>
        urlBuilder()
            .baseUrl(apiUrl)
            .project(projectName)
            .model(trainedModelName)
            .resource(':evaluate')
            .queryParam('api-version', LuisConstants.CustomText.AuthoringApiVersion)
            .build();

    const getVerificationUrl = (projectName: string, trainedModelName: string) =>
        urlBuilder()
            .baseUrl(apiUrl)
            .project(projectName)
            .model(trainedModelName)
            .resource('evaluation', 'result')
            .queryParam('api-version', apiVersion)
            .queryParam('stringIndexType', 'Utf16CodeUnit')
            .build();

    const getCustomTextTrainedModels = async ({
        projectName,
        nextLink
    }: {
        projectName: string;
        nextLink?: string;
    }): Promise<ResourceListResults<TrainedModelSwagger>> => {
        const url = nextLink || getUrlBuilder(projectName).build();

        const { data: trainedModels } = await httpClient.get<ResourceListResults<TrainedModelSwagger>>(url, apiOptions);

        return trainedModels;
    };

    const getTrainedModel = async ({
        projectName,
        trainingModelName
    }: {
        projectName: string;
        trainingModelName: string;
    }): Promise<TrainedModelSwagger> => {
        const url = getUrlBuilder(projectName, trainingModelName).build();

        const { data: trainedModel } = await httpClient.get<TrainedModelSwagger>(url, apiOptions);

        return trainedModel;
    };

    const getEvaluationData = async ({
        projectName,
        trainedModelName
    }: {
        projectName: string;
        trainedModelName: string;
    }): Promise<CustomTextModelEvaluationSwagger> => {
        const url = getUrlBuilder(projectName, trainedModelName)
            .resource('evaluation', 'summary-result')
            .build();

        const { data: evaluationData } = await httpClient.get<CustomTextModelEvaluationSwagger>(url, apiOptions);

        return evaluationData;
    };

    const getVerificationData = async ({
        nextLink,
        projectName,
        trainedModelName
    }: {
        nextLink?: string;
        projectName: string;
        trainedModelName: string;
    }): Promise<ResourceListResults<CustomTextModelVerificationSwagger>> => {
        let verificationData: ResourceListResults<CustomTextModelVerificationSwagger>;

        try {
            const url = nextLink ? `${nextLink}&stringIndexType=Utf16CodeUnit` : getVerificationUrl(projectName, trainedModelName);

            verificationData = (await httpClient.get<ResourceListResults<CustomTextModelVerificationSwagger>>(url, apiOptions)).data;
        } catch (e) {
            throw e;
        }

        return verificationData;
    };

    const getTrainedModelGuidanceReport = async ({
        projectName,
        trainedModelName
    }: {
        projectName: string;
        trainedModelName: string;
    }): Promise<ModelGuidanceReportSwagger> => {
        const url = getModelGuidanceUrl(projectName, trainedModelName);

        try {
            const { data: modelGuidanceReport } = await httpClient.get<ModelGuidanceReportSwagger>(url, apiOptions);
            return modelGuidanceReport;
        } catch (e) {
            return null;
        }
    };

    const deleteTrainedModel = async ({
        projectName,
        trainedModelName
    }: {
        projectName: string;
        trainedModelName: string;
    }): Promise<void> => {
        await httpClient.delete<void>(getUrlBuilder(projectName, trainedModelName).build(), apiOptions);
    };

    const loadModelSnapshotAndPoll = async ({
        projectName,
        trainedModelName,
        operationHandler
    }: {
        projectName: string;
        trainedModelName: string;
        operationHandler: OperationHandler<LoadSnapshotJobStatusSwagger>;
    }): Promise<void> => {
        const { headers } = await httpClient.post<void>(getLoadSnapshotUrl(projectName, trainedModelName), null, apiOptions);

        const statusUrl = (<Record<string, string>>headers)[LuisConstants.OperationHeaders.CTLocationHeader];

        await createOperationCall(statusUrl, apiOptions, operationHandler);
    };

    const initiateRefreshEvaluation = async ({
        projectName,
        trainedModelName,
        resourceId
    }: {
        projectName: string;
        trainedModelName: string;
        resourceId: string;
    }): Promise<string> => {
        const payload: EvaluationOnDemandJobInputSwagger = {
            kind: 'manual'
        };

        const modifiedApiOptions = luisRequestOptionsBuilder().useDefaultOptions(context.serviceKey, isProduction);

        if (!isProduction) {
            modifiedApiOptions.useCustomHeader('Azure-Resource-Id', resourceId).useCustomHeader('apim-product-id', 'TextAnalytics.S');
        }

        const { headers } = await httpClient.post<void>(
            getRefreshEvaluationUrl(projectName, trainedModelName),
            payload,
            modifiedApiOptions.build()
        );

        const statusUrl = (<Record<string, string>>headers)[LuisConstants.OperationHeaders.CTLocationHeader];

        return statusUrl;
    };

    const pollRefreshEvaluation = async ({
        statusUrl,
        jobId,
        projectName,
        operationHandler
    }: {
        statusUrl?: string;
        jobId?: string;
        projectName?: string;
        operationHandler: OperationHandler<EvaluationOnDemandJobSwagger>;
    }): Promise<void> => {
        if (!statusUrl) {
            statusUrl = getUrlBuilder(projectName)
                .resource('evaluate')
                .resource('jobs', jobId)
                .build();
        }

        await createOperationCallWithJobSwaggers(statusUrl, apiOptions, operationHandler);
    };

    return {
        getCustomTextTrainedModels,
        getTrainedModel,
        getTrainedModelGuidanceReport,
        getEvaluationData,
        getVerificationData,
        deleteTrainedModel,
        loadModelSnapshotAndPoll,
        initiateRefreshEvaluation,
        pollRefreshEvaluation
    };
});
